/*
  <sticky-add-to-cart> is the bar rendered by snippets/product-sticky-bar.liquid.

  It slides in once the product form's own buttons have scrolled above the
  viewport and slides away again when they return or when the footer comes
  into view. It never adds to the cart itself: its button submits the product
  form as if the main button had been pressed, and it mirrors that button's
  label, disabled and loading states, so there is one source of truth.

  While it is on screen the bar publishes its height as --sticky-bar-height on
  the root element, which other fixed controls use to sit above it. The space
  it may cover is reserved at the end of the page through --sticky-bar-space.
*/
if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-sticky-button]');
      this.label = this.querySelector('[data-sticky-label]');
      this.form = document.getElementById(this.dataset.formId);
      if (!this.button || !this.form || !('IntersectionObserver' in window)) return;

      this.pastButtons = false;
      this.footerReached = false;
      this.wasLoading = false;
      this.submitted = false;

      this.onClick = this.onClick.bind(this);
      this.button.addEventListener('click', this.onClick);

      this.stopListening = Theme.on('variant:changed', (event) => this.onVariantChange(event));

      /* The add button itself is replaced on every variant change, so its wrapper is watched instead. */
      this.buttonsTarget = this.form.querySelector('.product-form__buttons') || this.form;
      this.footerTarget = document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer');

      this.viewObserver = new IntersectionObserver((entries) => this.onIntersect(entries));
      this.viewObserver.observe(this.buttonsTarget);
      if (this.footerTarget) this.viewObserver.observe(this.footerTarget);

      /* Scoped to the product form: follows the main button and the error message. */
      this.formRoot = this.form.closest('product-form') || this.form;
      this.formObserver = new MutationObserver(() => this.mirror());
      this.formObserver.observe(this.formRoot, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'disabled', 'hidden'],
      });

      if ('ResizeObserver' in window) {
        this.sizeObserver = new ResizeObserver(() => this.publishSize());
        this.sizeObserver.observe(this);
      }
      this.publishSize();
      this.mirror();
    }

    disconnectedCallback() {
      if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
      if (this.stopListening) this.stopListening();
      if (this.viewObserver) this.viewObserver.disconnect();
      if (this.formObserver) this.formObserver.disconnect();
      if (this.sizeObserver) this.sizeObserver.disconnect();
      document.documentElement.style.removeProperty('--sticky-bar-height');
      document.documentElement.style.removeProperty('--sticky-bar-space');
    }

    get mainButton() {
      return this.form.querySelector('[data-product-region="add-button"]');
    }

    get visible() {
      return this.classList.contains('is-visible');
    }

    /* Visibility */

    onIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.target === this.buttonsTarget) {
          this.pastButtons = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        } else if (entry.target === this.footerTarget) {
          /* Still "reached" when the footer's top edge has moved above the viewport. */
          this.footerReached = entry.isIntersecting || entry.boundingClientRect.top < 0;
        }
      });
      this.toggle(this.pastButtons && !this.footerReached);
    }

    toggle(show) {
      if (show === this.visible) return;

      if (!show && this.contains(document.activeElement)) {
        /* Focus must not be left on a control that is about to become inert. */
        const main = this.mainButton;
        if (main) main.focus({ preventScroll: true });
      }

      this.classList.toggle('is-visible', show);
      this.toggleAttribute('inert', !show);
      this.publishSize();
    }

    publishSize() {
      const root = document.documentElement;
      const height = Math.ceil(this.getBoundingClientRect().height);
      if (height > 0) root.style.setProperty('--sticky-bar-space', `${height}px`);
      if (this.visible && height > 0) root.style.setProperty('--sticky-bar-height', `${height}px`);
      else root.style.removeProperty('--sticky-bar-height');
    }

    /* Content */

    onVariantChange(event) {
      const detail = event.detail || {};
      if (detail.sectionId !== this.dataset.sectionId) return;
      const variant = detail.variant;

      const title = this.querySelector('[data-sticky-variant]');
      if (title) {
        title.textContent = variant ? variant.title : '';
        title.hidden = !variant;
      }

      const price = this.querySelector('[data-sticky-price]');
      if (price) {
        price.textContent = variant ? Theme.formatMoney(variant.price) : '';
        price.hidden = !variant;
      }

      const image = this.querySelector('.sticky-bar__image');
      const source = variant && variant.featured_image ? variant.featured_image.src : '';
      if (image && source) {
        const url = new URL(source, window.location.origin);
        url.searchParams.set('width', '200');
        image.removeAttribute('srcset');
        image.src = url.toString();
      }

      let text = Theme.strings.unavailable;
      if (variant) text = variant.available ? Theme.strings.addToCart : Theme.strings.soldOut;
      this.setButton(Boolean(variant && variant.available), text, false);
    }

    /* Copies the main button's state; runs whenever the product form changes. */
    mirror() {
      const main = this.mainButton;
      if (!main) return;

      const mainLabel = main.querySelector('[data-add-label]') || main;
      const loading = main.classList.contains('is-loading');
      this.setButton(!main.disabled, mainLabel.textContent.trim(), loading);

      if (this.submitted && this.wasLoading && !loading) {
        this.submitted = false;
        this.revealError();
      }
      this.wasLoading = loading;
    }

    setButton(enabled, text, loading) {
      this.button.disabled = !enabled;
      if (this.label && text) this.label.textContent = text;
      this.button.classList.toggle('is-loading', loading);
      if (loading) this.button.setAttribute('aria-disabled', 'true');
      else this.button.removeAttribute('aria-disabled');
    }

    /* The form shows its error next to the main button, which is off screen while the bar is in use. */
    revealError() {
      const error = this.formRoot.querySelector('[data-form-error]');
      if (!error || error.hidden || !error.textContent.trim()) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      error.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    /* Submitting */

    onClick(event) {
      const main = this.mainButton;
      /* Without requestSubmit the button's form attribute posts the form the ordinary way. */
      if (!main || typeof this.form.requestSubmit !== 'function') return;

      event.preventDefault();
      if (this.button.disabled || main.disabled || this.button.classList.contains('is-loading')) return;

      this.submitted = true;
      this.form.requestSubmit(main);
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
